import Button from 'components/base/Button';
import React, {
  ChangeEvent,
  useRef,
  useState,
  useEffect,
  KeyboardEvent
} from 'react';
import { Form } from 'react-bootstrap';
import authenticatorimg from 'assets/img/spot-illustrations/authenticator.png';

const totalInputLength = 6;

const TwoFAForm = ({
  onSubmit,
  qrCodeURL,
  tfaConfirmed,
  username
}: {
  onSubmit: (username: string, otp: string) => void;
  qrCodeURL: string;
  tfaConfirmed: boolean;
  username: string;
}) => {
  const [otp, setOtp] = useState(Array(totalInputLength).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus on the first textbox when the component loads
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement>,
    index: number
  ): void => {
    const { value } = e.target;
    const updatedOtp = [...otp];

    if (value) {
      updatedOtp[index] = value[0];
      setOtp(updatedOtp);
      if (index < totalInputLength - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    } else {
      updatedOtp[index] = '';
      setOtp(updatedOtp);
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleSubmit = () => {
    const otpValue = otp.join('');
    if (otpValue.length === totalInputLength) {
      onSubmit(username, otpValue);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Enter' && index === totalInputLength - 1) {
      handleSubmit();
    }
  };

  return (
    <div className="px-xxl-5">
      <div className="text-center">
        <h4 className="text-body-highlight">Two Factor Authentication</h4>
        {!tfaConfirmed && (
          <>
            <img src={qrCodeURL} alt="QR Code" className="mb-0 pt-2" />
            <p className="text-body-tertiary text-align-left pt-0 fs-10 mb-0">
              Scan this QR code from your TFA App
            </p>
            <p className="text-body-tertiary text-center pt-3 mb-0">
              This is your first time using TFA. Please, scan the QR above and
              then introduce your verification code below.
            </p>
          </>
        )}
        {tfaConfirmed && (
          <>
            <img src={authenticatorimg} alt="Auth App" className="mb-0 pt-2" />
            <p className="text-body-tertiary text-center pt-3 mb-0">
              Introduce your verification code below.
            </p>
          </>
        )}
        <div className="verification-form mt-4">
          <div className="d-flex align-items-center gap-2 mb-6">
            {Array(totalInputLength)
              .fill('')
              .map((_, index) => (
                <React.Fragment key={index}>
                  <Form.Control
                    ref={(el: HTMLInputElement) => {
                      inputRefs.current[index] = el;
                    }}
                    className="px-2 text-center pt-1 pb-0 fs-6"
                    type="text"
                    maxLength={1}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleChange(e, index)
                    }
                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
                      handleKeyDown(e, index)
                    }
                    value={otp[index]}
                  />
                  {index === 2 && <span>-</span>}
                </React.Fragment>
              ))}
          </div>
          <Button
            variant="primary"
            className="w-100 mb-5"
            type="button"
            onClick={handleSubmit}
            disabled={otp.join('').length < totalInputLength}
          >
            Verify
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TwoFAForm;
